import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { MouseEvent, PointerEvent } from 'react';

import { DataExplorerToolbarActions } from './DataExplorerToolbarActions';
import type { DataExplorerImagePreviewProps } from './types';
import { resolveDataExplorerAppearance } from './theme';
import { cn } from './utils';

type NaturalSize = { width: number; height: number };

const DEFAULT_ZOOM_STEP = 1.18;
const DEFAULT_STAGE_PADDING = 16;
const MAX_SAFE_SCALE = 1e6;
const MIN_SAFE_SCALE = 1e-6;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const isNoDragTarget = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest('[data-no-drag="true"]'));

function ResetIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v6h6" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M5 12h14" />
    </svg>
  );
}

export function DataExplorerImagePreview({
  src,
  alt = 'Preview image',
  empty = 'No image preview',
  actions,
  actionContext,
  toolbarActions,
  zoomStep = DEFAULT_ZOOM_STEP,
  stagePadding = DEFAULT_STAGE_PADDING,
  minScale = MIN_SAFE_SCALE,
  maxScale = MAX_SAFE_SCALE,
  controlsVisible = true,
  zoomLabelVisible = false,
  resetLabel = 'Reset image view',
  zoomInLabel = 'Zoom in',
  zoomOutLabel = 'Zoom out',
  appearance,
  className,
  classNames,
  onLoad,
  onError,
}: DataExplorerImagePreviewProps) {
  const theme = resolveDataExplorerAppearance(
    appearance ?? { background: 'dark-stage' },
  );
  const imageSrc = typeof src === 'string' ? src.trim() : '';
  const stageRef = useRef<HTMLDivElement>(null);
  const stageSizeRef = useRef<{ width: number; height: number } | null>(null);
  const viewRef = useRef({ scale: 1, translateX: stagePadding, translateY: stagePadding });
  const draggingRef = useRef<
    | {
        pointerId: number;
        startClientX: number;
        startClientY: number;
        startTranslateX: number;
        startTranslateY: number;
      }
    | undefined
  >(undefined);
  const [naturalSize, setNaturalSize] = useState<NaturalSize | null>(null);
  const [dragging, setDragging] = useState(false);
  const [{ scale, translateX, translateY }, setView] = useState(() => ({
    scale: 1,
    translateX: stagePadding,
    translateY: stagePadding,
  }));

  useEffect(() => {
    viewRef.current = { scale, translateX, translateY };
  }, [scale, translateX, translateY]);

  useEffect(() => {
    setNaturalSize(null);
    setView({ scale: 1, translateX: stagePadding, translateY: stagePadding });
  }, [imageSrc, stagePadding]);

  const normalizedMinScale = Number.isFinite(minScale)
    ? Math.max(MIN_SAFE_SCALE, minScale)
    : MIN_SAFE_SCALE;
  const normalizedMaxScale = Number.isFinite(maxScale)
    ? Math.max(normalizedMinScale, maxScale)
    : MAX_SAFE_SCALE;
  const normalizedZoomStep =
    Number.isFinite(zoomStep) && zoomStep > 1 ? zoomStep : DEFAULT_ZOOM_STEP;
  const normalizedStagePadding =
    Number.isFinite(stagePadding) && stagePadding >= 0
      ? stagePadding
      : DEFAULT_STAGE_PADDING;

  const resetView = useCallback(
    (nextNaturalSize?: NaturalSize) => {
      const stage = stageRef.current;
      const size = nextNaturalSize ?? naturalSize;
      if (!stage || !size) return;

      const availableWidth = Math.max(
        0,
        stage.clientWidth - normalizedStagePadding * 2,
      );
      const availableHeight = Math.max(
        0,
        stage.clientHeight - normalizedStagePadding * 2,
      );
      if (
        availableWidth <= 0 ||
        availableHeight <= 0 ||
        size.width <= 0 ||
        size.height <= 0
      ) {
        return;
      }

      const fitScale = Math.min(
        availableWidth / size.width,
        availableHeight / size.height,
      );
      const nextScale = clamp(
        fitScale,
        normalizedMinScale,
        normalizedMaxScale,
      );
      const fittedWidth = size.width * nextScale;
      const fittedHeight = size.height * nextScale;

      setView({
        scale: nextScale,
        translateX: (stage.clientWidth - fittedWidth) / 2,
        translateY: (stage.clientHeight - fittedHeight) / 2,
      });
    },
    [naturalSize, normalizedMaxScale, normalizedMinScale, normalizedStagePadding],
  );

  const compensateStageResize = useCallback((width: number, height: number) => {
    if (!Number.isFinite(width) || !Number.isFinite(height)) return;
    if (width <= 0 || height <= 0) return;

    const previous = stageSizeRef.current;
    stageSizeRef.current = { width, height };
    if (!previous || !imageSrc) return;

    const deltaX = (width - previous.width) / 2;
    const deltaY = (height - previous.height) / 2;
    if (deltaX === 0 && deltaY === 0) return;

    setView((current) => ({
      scale: current.scale,
      translateX: current.translateX + deltaX,
      translateY: current.translateY + deltaY,
    }));
  }, [imageSrc]);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    compensateStageResize(stage.clientWidth, stage.clientHeight);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        compensateStageResize(entry.contentRect.width, entry.contentRect.height);
      });
      observer.observe(stage);
      return () => observer.disconnect();
    }

    const handleResize = () => {
      compensateStageResize(stage.clientWidth, stage.clientHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [compensateStageResize]);

  const zoomAtPoint = useCallback(
    (factor: number, stageX: number, stageY: number) => {
      if (!Number.isFinite(factor) || factor === 1) return;
      if (!Number.isFinite(stageX) || !Number.isFinite(stageY)) return;

      setView((current) => {
        const nextScale = clamp(
          current.scale * factor,
          normalizedMinScale,
          normalizedMaxScale,
        );
        if (!Number.isFinite(nextScale) || nextScale === current.scale) {
          return current;
        }

        const scaleRatio = nextScale / current.scale;
        return {
          scale: nextScale,
          translateX: stageX - (stageX - current.translateX) * scaleRatio,
          translateY: stageY - (stageY - current.translateY) * scaleRatio,
        };
      });
    },
    [normalizedMaxScale, normalizedMinScale],
  );

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !imageSrc || !naturalSize) return;

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey) return;

      event.preventDefault();
      const rect = stage.getBoundingClientRect();
      const stageX = event.clientX - rect.left;
      const stageY = event.clientY - rect.top;
      const factor = Math.pow(normalizedZoomStep, -event.deltaY / 100);
      zoomAtPoint(factor, stageX, stageY);
    };

    stage.addEventListener('wheel', handleWheel, { passive: false });
    return () => stage.removeEventListener('wheel', handleWheel);
  }, [imageSrc, naturalSize, normalizedZoomStep, zoomAtPoint]);

  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      if (!imageSrc || !naturalSize) return;
      if (isNoDragTarget(event.target)) return;

      const stage = stageRef.current;
      if (!stage) return;

      event.preventDefault();
      stage.setPointerCapture(event.pointerId);
      draggingRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startTranslateX: viewRef.current.translateX,
        startTranslateY: viewRef.current.translateY,
      };
      setDragging(true);
    },
    [imageSrc, naturalSize],
  );

  const handlePointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const drag = draggingRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    event.preventDefault();
    setView({
      scale: viewRef.current.scale,
      translateX: drag.startTranslateX + event.clientX - drag.startClientX,
      translateY: drag.startTranslateY + event.clientY - drag.startClientY,
    });
  }, []);

  const endDrag = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const drag = draggingRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    draggingRef.current = undefined;
    setDragging(false);

    const stage = stageRef.current;
    if (stage?.hasPointerCapture(event.pointerId)) {
      stage.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleDoubleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!imageSrc || !naturalSize) return;
      if (isNoDragTarget(event.target)) return;
      resetView();
    },
    [imageSrc, naturalSize, resetView],
  );

  const handleZoomButtonClick = useCallback(
    (factor: number) => {
      const stage = stageRef.current;
      if (!stage || !naturalSize) return;
      zoomAtPoint(factor, stage.clientWidth / 2, stage.clientHeight / 2);
    },
    [naturalSize, zoomAtPoint],
  );

  return (
    <div
      ref={stageRef}
      className={cn(
        theme.slots.previewRoot,
        imageSrc && naturalSize
          ? dragging
            ? 'cursor-grabbing'
            : 'cursor-grab'
          : 'cursor-default',
        classNames?.root,
        className,
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={handleDoubleClick}
      style={theme.styles.previewRoot}
    >
      {imageSrc ? (
        <div
          className={cn(
            'absolute left-0 top-0 will-change-transform',
            classNames?.imageLayer,
          )}
          style={{
            transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
            transformOrigin: '0 0',
          }}
        >
          <img
            src={imageSrc}
            alt={alt}
            draggable={false}
            className={cn(
              'pointer-events-none max-h-none max-w-none select-none',
              classNames?.image,
            )}
            onLoad={(event) => {
              const width = event.currentTarget.naturalWidth;
              const height = event.currentTarget.naturalHeight;
              if (!width || !height) return;
              const size = { width, height };
              setNaturalSize(size);
              resetView(size);
              onLoad?.(size);
            }}
            onError={onError}
          />
        </div>
      ) : (
        <div
          className={cn(
            theme.slots.previewPlaceholder,
            classNames?.placeholder,
          )}
        >
          {empty}
        </div>
      )}

      {toolbarActions?.length || actions ? (
        <div
          data-no-drag="true"
          className={cn(
            theme.slots.previewActions,
            classNames?.actions,
          )}
        >
          {toolbarActions?.length ? (
            <DataExplorerToolbarActions
              context={actionContext}
              actions={toolbarActions}
              layout="inline"
              appearance={appearance ?? { background: 'dark-stage' }}
            />
          ) : null}
          {actions}
        </div>
      ) : null}

      {controlsVisible ? (
        <div
          data-no-drag="true"
          className={cn(
            theme.slots.previewControls,
            classNames?.controls,
          )}
        >
          {zoomLabelVisible ? (
            <span
              className={cn(
                theme.slots.previewZoomLabel,
                classNames?.zoomLabel,
              )}
            >
              {Math.round(scale * 100)}%
            </span>
          ) : null}
          <button
            type="button"
            aria-label={zoomOutLabel}
            disabled={!imageSrc || !naturalSize}
            className={cn(
              theme.slots.previewControlButton,
              'disabled:cursor-not-allowed disabled:opacity-40',
              classNames?.controlButton,
            )}
            onClick={() => handleZoomButtonClick(1 / normalizedZoomStep)}
          >
            <MinusIcon />
          </button>
          <button
            type="button"
            aria-label={zoomInLabel}
            disabled={!imageSrc || !naturalSize}
            className={cn(
              theme.slots.previewControlButton,
              'disabled:cursor-not-allowed disabled:opacity-40',
              classNames?.controlButton,
            )}
            onClick={() => handleZoomButtonClick(normalizedZoomStep)}
          >
            <PlusIcon />
          </button>
          <button
            type="button"
            aria-label={resetLabel}
            disabled={!imageSrc || !naturalSize}
            className={cn(
              theme.slots.previewControlButton,
              'disabled:cursor-not-allowed disabled:opacity-40',
              classNames?.controlButton,
            )}
            onClick={() => resetView()}
          >
            <ResetIcon />
          </button>
        </div>
      ) : null}
    </div>
  );
}
